import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ContactInput, ContactListParams, ImportRowsInput } from '@/lib/types';

export const contactKeys = {
  all: (jobId: string) => ['jobs', jobId, 'contacts'] as const,
  list: (jobId: string, params: ContactListParams) => ['jobs', jobId, 'contacts', 'list', params] as const,
  tags: (jobId: string) => ['jobs', jobId, 'contacts', 'tags'] as const,
  stats: (jobId: string) => ['jobs', jobId, 'contacts', 'stats'] as const,
  imports: (jobId: string) => ['jobs', jobId, 'contacts', 'imports'] as const,
};

export function useContacts(jobId: string, params: ContactListParams) {
  return useQuery({ queryKey: contactKeys.list(jobId, params), queryFn: () => api.contacts.list(jobId, params), placeholderData: keepPreviousData });
}

export function useTags(jobId: string) {
  return useQuery({ queryKey: contactKeys.tags(jobId), queryFn: () => api.contacts.tags(jobId) });
}

export function useContactStats(jobId: string) {
  return useQuery({ queryKey: contactKeys.stats(jobId), queryFn: () => api.contacts.stats(jobId) });
}

export function useImports(jobId: string, enabled = true) {
  return useQuery({ queryKey: contactKeys.imports(jobId), queryFn: () => api.contacts.imports(jobId), enabled });
}

function useInvalidateContacts(jobId: string) {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: contactKeys.all(jobId) }), qc.invalidateQueries({ queryKey: ['jobs', 'detail', jobId] }), qc.invalidateQueries({ queryKey: ['jobs', 'list'] })]);
}

export function useCreateContact(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({ mutationFn: (body: ContactInput) => api.contacts.create(jobId, body), onSuccess: () => invalidate() });
}

export function useUpdateContact(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: ContactInput & { optedOut?: boolean } }) => api.contacts.update(jobId, contactId, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteContact(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({ mutationFn: (contactId: string) => api.contacts.remove(jobId, contactId), onSuccess: () => invalidate() });
}

export function useBulkDeleteContacts(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({ mutationFn: (ids: string[]) => api.contacts.bulkDelete(jobId, ids), onSuccess: () => invalidate() });
}

export function useBulkTags(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({
    mutationFn: ({ ids, add, remove }: { ids: string[]; add: string[]; remove: string[] }) => api.contacts.bulkTags(jobId, ids, add, remove),
    onSuccess: () => invalidate(),
  });
}

export function useImportContacts(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({
    mutationFn: (formData: FormData) => api.contacts.import(jobId, formData),
    onSuccess: (res) => (res.dryRun ? undefined : invalidate()),
  });
}

/** Parsed-rows import (the workbook importer). Dry runs never invalidate. */
export function useImportRows(jobId: string) {
  const invalidate = useInvalidateContacts(jobId);
  return useMutation({
    mutationFn: (body: ImportRowsInput) => api.contacts.importRows(jobId, body),
    onSuccess: (res) => (res.dryRun ? undefined : invalidate()),
  });
}

export function useContactConsents(jobId: string, contactId: string | null) {
  return useQuery({ queryKey: ['jobs', jobId, 'contacts', 'consents', contactId], queryFn: () => api.contacts.consents(jobId, contactId as string), enabled: !!contactId });
}
